import {
  AccountBalance as BankIcon,
  BusinessCenter as BusinessIcon,
  Close as CloseIcon,
  EditOutlined as EditIcon,
  LocationOn as LocationIcon,
  Money as MoneyIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  styled,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { sendOTP } from "../../services/loanService";
import { getAllRoles, getArea, getRegion } from "../../services/userService";
import OTPVerification from "../borrowers/OTPVerification";
import { useSelector } from "react-redux";
import { ip, coordinates } from "../../Store/userSlice";
import { HIDE_INDIVIDUAL_BORROWER_OPTION } from "../../utils/constants";

// Borrower Types
const BORROWER_TYPES = [
  { value: "none", label: "None" },
  { value: "individual", label: "Individual" },
  { value: "entity", label: "Entity" },
];

const ENTITY_TYPES = [
  { value: "company", label: "Company" },
  { value: "partnership_firm", label: "Partnership Firm" },
  { value: "propreitorship_firm", label: "Proprietorship Firm" },
];

// Manager's Lender Types
const LENDER_TYPES = [
  { value: "", label: "None" },
  { value: "PERSONAL", label: "Personal Loan" },
  { value: "BUSINESS", label: "Business Loan" },
  { value: "HOME", label: "Home Loan" },
  { value: "VEHICLE", label: "Vehicle Loan" },
  { value: "EDUCATION", label: "Education Loan" },
];

const FINANCIER_TYPES = [
  { value: "BANK", label: "Bank" },
  { value: "NBFC", label: "NBFC" },
  { value: "CREDIT_UNION", label: "Credit Union" },
  { value: "PRIVATE", label: "Private Lender" },
  { value: "GOVERNMENT", label: "Government" },
];

// Styled components for form elements
const StyledTextField = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    width: '100%',
    borderRadius: 12,
    transition: 'all 0.3s ease',
    backgroundColor: alpha(theme.palette.background.paper, 0.7),
    '& fieldset': {
      borderColor: alpha(theme.palette.divider, 0.3),
      transition: 'all 0.3s ease',
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.primary.main, 0.5),
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1.5px',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 500,
    fontSize: '0.9rem',
  },
  '& .MuiOutlinedInput-input': {
    padding: '14px 16px',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.95rem',
    width: '100%',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 4,
    marginTop: 4,
    fontFamily: "'Poppins', sans-serif",
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  width: '100%',
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.paper, 0.7),
  '& .MuiSelect-select': {
    padding: '14px 16px',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '0.95rem',
    width: '100%',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.divider, 0.3),
    transition: 'all 0.3s ease',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: alpha(theme.palette.primary.main, 0.5),
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.primary.main,
    borderWidth: '1.5px',
  },
}));

const FormSectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  fontWeight: 600,
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50%",
    transform: "translateY(-50%)",
    width: 3,
    height: "70%",
    backgroundColor: theme.palette.primary.main,
    borderRadius: 4,
  },
}));

const UserForm = ({
  open,
  handleClose,
  onSubmit,
  user,
  mode = "add",
  userType = "manager",
  error: externalError,
  setError: setExternalError,
}) => {
  // Determine initial form values based on userType
  const defaultValues = {
    uuid: user?.uuid || "",
    username: user?.username || "",
    email: user?.email || "",
    name: user?.name || "",
    phoneNumber: user?.phoneNumber || user?.mobileNumber || "",
    roleId: user?.roleId || "",
    region: user?.region || "",
    area: user?.area || "",
    userType: mode === "add" ? "none" : (user?.borrowerType || "individual"),
    entityType: user?.entityType || "",
    // Only include financierType for managers
    ...(userType === "manager" && { financierType: user?.financierType || "" }),
    ...(userType === "consultant" && {
      companyName: user?.companyName || "",
      website: user?.website || "",
      yearsExperience: user?.yearsExperience || "",
      primarySpecializations: user?.primarySpecializations || [],
      specializationOther: user?.specializationOther || "",
    }),
    // Borrower specific fields
    ...(userType === "borrower" && {
      gstNumber: user?.gstNumber || "",
      address: user?.address || "",
      entityName: user?.entityName || "",
    }),
    ...(userType === "lender" && {
      financialInstitutionType: user?.financialInstitutionType || "",
      financialInstitution: user?.financialInstitution || "",
      typeOfLending: user?.typeOfLending || "",
      typeOfRetailLoans: user?.typeOfRetailLoans || "",
      designation: user?.designation || "",
      location: user?.location || "",
    }),
    userRole: userType === "manager" ? "MANAGER" : userType === "borrower" ? "BORROWER" : "LENDER",
  };
    const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
    unregister
  } = useForm({
    defaultValues,
    mode: 'onChange',
  });

  const theme = useTheme();
  const [roles, setRoles] = useState([]);
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [selectedFile, setSelectedFile] = useState(null);
  const [existingFileUrl, setExistingFileUrl] = useState(null);
  const fileInputRef = useRef(null);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [otpRequestData, setOtpRequestData] = useState(null);
  const [showMobileOTP, setShowMobileOTP] = useState(true);
  const [formData, setFormData] = useState(null);
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedUserType, setSelectedUserType] = useState(mode === "add" ? "none" : (user?.borrowerType || "individual"));

  const IpAddress = useSelector(ip);
  const userCoordinates = useSelector(coordinates);

useEffect(() => {
  if (user?.userType) {
    setSelectedUserType(user.userType);
  }
}, [user]);

  useEffect(() => {
    if (userType !== "manager") {
      unregister("username");
    }
    if (userType !== "borrower") {
      unregister("userType");
    }
  }, [userType, unregister]);

  useEffect(() => {
    if (open && user && mode === "edit") {
        const resetValues = {
          uuid: user?.uuid || "",
          username: user?.username || "",
          email: user?.email || "",
          name: user?.name || "",
          phoneNumber: user?.phoneNumber || user?.mobileNumber || "",
          roleId: user?.roleId || "",
          region: user?.region || "",
          area: user?.area || "",
        };

        if (userType === "borrower") {
          resetValues.gstNumber = user?.gstNumber || "";
          resetValues.address = user?.address || "";
          resetValues.entityName = user?.entityName || "";
        
        resetValues.userType = user?.borrowerType === "entity" ? "entity" : "individual";

        if (user?.borrowerType === "entity") {
          resetValues.entityType = user?.entityType || "";
        }
      }

      if (userType === "lender") {
        resetValues.financialInstitutionType = user?.financialInstitutionType || "";
        resetValues.name = user?.name || "";
        resetValues.financialInstitution = user?.financialInstitution || "";
        resetValues.typeOfLending = user?.typeOfLending || "";
        resetValues.typeOfRetailLoans = user?.typeOfRetailLoans || "";
        resetValues.designation = user?.designation || "";
        resetValues.location = user?.location || "";
      }

      if (userType === "consultant") {
        resetValues.companyName = user?.companyName || "";
        resetValues.website = user?.website || user?.professionalWebsite || "";
        resetValues.yearsExperience = user?.yearsExperience || user?.yearsOfExperience || "";
        resetValues.primarySpecializations = user?.primarySpecializations || user?.loanSpecialization || [];
        resetValues.specializationOther = user?.specializationOther || user?.otherSpecialization || "";
      }

      reset(resetValues);
      setSelectedUserType(resetValues.userType);
    } 
    else if (open && !user) {
        reset({
            userType: userType === "borrower" ? "individual" : "",
            entityType: "",
            ...(userType === "manager" && { financierType: "" }),
        });
    }
  }, [open, user, mode, reset, userType]);

  useEffect(() => {
    if (selectedRole === "AREA_MANAGER" && user?.region) {
      const areaData = getArea(user?.region);
      setAreas(areaData?.result || []);
    }
  }, [selectedRole, user?.region])


  const fetchAreas = async (region) => {
    try {
      const areaData = await getArea(region);
    setAreas(areaData?.result || []);
    } catch (error) {
      console.error("Error fetching areas:", error);
      setAreas([]);
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'region' && value.region) {
        fetchAreas(value.region);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Reset form when dialog closes
  useEffect(() => {
    if (open) {
      reset({
        ...defaultValues,
        userType: mode === "add" ? "none" : (user?.borrowerType || "individual"),
      });
      setSelectedUserType(mode === "add" ? "none" : (user?.borrowerType || "individual"));
    }
  }, [open]);

  useEffect(() => {
    if (user) {
      reset({
        ...defaultValues,
        userType: mode === "add" ? "none" : (user?.borrowerType || "individual"),
      });
      setSelectedUserType(mode === "add" ? "none" : (user?.borrowerType || "individual"));
    }
  }, [user, reset]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (userType === "manager") {
          const rolesData = await getAllRoles();
          setRoles(rolesData || []);
        } else {
          setRoles([]);
        }

        if (userType === "manager" || userType === "lender") {
          const locationsData = await getRegion();
          setRegions(locationsData?.result || []);
          
          if (userType === "manager" && mode === "edit" && user?.region) {
            const areaData = await getArea(user.region);
            setAreas(areaData?.result || []);
          }
        } else {
          setRegions([]);
          setAreas([]);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(
          error.message || "Failed to load required data. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchData();
    }
  }, [open, userType, mode, user?.region]);

  // On component mount, set the existing file URL if available
  useEffect(() => {
    if (user?.profilePhotoUrl) {
      setExistingFileUrl(user.profilePhotoUrl);
    } else {
      setExistingFileUrl(null);
    }
  }, [user]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFormSubmit = async (data) => {
    if (userType === "borrower" && data.userType === "none") {
      setError("Please select a borrower type (Individual or Entity)");
      return;
    }

    if (userType === "borrower" && mode === "add") {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const isSuperAdmin = user.role === 'SUPERADMIN';

      if (isSuperAdmin) {
        handleSubmitForm(data);
      } else {
      const formDataToStore = {
        ...data,
        ip: IpAddress,
        longitude: userCoordinates?.lng || "",
        latitude: userCoordinates?.lat || "",
      };
      setFormData(formDataToStore);
      const otpData = {
        mobileNumber: data.phoneNumber,
        email: data.email,
        ip: IpAddress,
        longitude: userCoordinates?.lng || "",
        latitude: userCoordinates?.lat || "",
      };

      setOtpRequestData(otpData);
      setShowOTPVerification(true);

      try {
        const response = await sendOTP(otpData);
        if (response?.responseCode === 200) {
          setError(null);
        } else {
          setError(response?.responseMessage || "Failed to send OTP");
        }
      } catch (err) {
        setError("Failed to send OTP");
        console.error("Send OTP error:", err);
        }
      }
    } else {
      handleSubmitForm(data);
    }
  };

  const handleOTPVerificationSuccess = () => {
    if (formData) {
      handleSubmitForm(formData);
    }
  };

  const handleOTPVerificationError = (message) => {
    setError(message);
  };

  const handleSubmitForm = async (data) => {
    setLoading(true);
    setError(null);
    try {
      // Transform data for borrower type
      if (userType === "borrower") {
        const transformedData = {
          ...data,
          borrowerType: data.userType === "entity" ? "entity" : "individual",
          entityType: data.userType === "entity" ? data.entityType : undefined,
          mobileNumber: data.phoneNumber,
          gstNumber: data.gstNumber || "",
          file: selectedFile,
          ip: IpAddress,
          longitude: userCoordinates?.lng || "",
          latitude: userCoordinates?.lat || "",
        };
        await onSubmit(transformedData);
      } else {
        await onSubmit(data);
      }
    } catch (err) {
      setError(err.message || "Failed to submit form");
    } finally {
      setLoading(false);
    }
  };

  // Select appropriate title based on mode and userType
  const formTitle =
    mode === "add"
      ? `Add New ${userType === "manager" ? "Manager" : userType === "consultant" ? "Consultant" : userType === "borrower" ? "Borrower" : "Lender"}`
      : `Edit ${userType === "manager" ? "Manager" : userType === "consultant" ? "Consultant" : userType === "borrower" ? "Borrower" : "Lender"}`;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "24px",
            boxShadow:
              "0 20px 60px rgba(0,0,0,0.15), 0 12px 30px rgba(0,0,0,0.08)",
            overflow: "hidden",
            backgroundImage:
              theme.palette.mode === "light"
                ? "linear-gradient(to bottom, #ffffff, #f8faff)"
                : "linear-gradient(to bottom, #1a1f2e, #131723)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            maxHeight: "90vh", // Limit dialog height to viewport
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "5px",
              background: `linear-gradient(90deg, 
                            ${theme.palette.primary.dark} 0%, 
                            ${theme.palette.primary.main} 50%,
                            ${theme.palette.primary.dark} 100%)`,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            background: alpha(theme.palette.primary.main, 0.04),
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                color: theme.palette.primary.main,
                mr: 2,
                width: 42,
                height: 42,
              }}
            >
              {mode === "add" ? <PersonAddIcon /> : <EditIcon />}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                component="div"
                fontWeight="bold"
                sx={{
                  color: theme.palette.text.primary,
                  fontFamily: "'Poppins', sans-serif",
                  letterSpacing: "0.02em",
                  fontSize: "1.125rem",
                }}
              >
                {formTitle}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.8),
                  fontFamily: "'Poppins', sans-serif",
                  display: "block",
                  mt: 0.5,
                }}
              >
                {userType === "borrower"
                  ? "Borrower Account"
                  : userType === "lender"
                    ? "Lender Account"
                  : userType === "consultant"
                    ? "Consultant Account"
                    : "Manager Account"}
              </Typography>
            </Box>
          </Box>
          <IconButton
            edge="end"
            onClick={handleClose}
            aria-label="close"
            sx={{
              color: alpha(theme.palette.primary.main, 0.8),
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              transition: "all 0.2s",
              "&:hover": {
                bgcolor: alpha(theme.palette.primary.main, 0.15),
                transform: "rotate(90deg)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleFormSubmit, (errors) => {
            console.log("Validation errors:", errors);
          })
          }
          style={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minHeight: 0,
          }}
        >
          <DialogContent
            sx={{
              p: 4,
              background: alpha(theme.palette.background.paper, 0.6),
              backdropFilter: "blur(10px)",
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              '& .MuiGrid-root': {
                width: '100%',
                margin: 0,
                padding: 0
              },
              '& .MuiGrid-item': {
                width: '100%',
                padding: theme.spacing(1.25, 0),
                boxSizing: 'border-box'
              },
              "&::-webkit-scrollbar": {
                width: "8px",
                borderRadius: "8px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                borderRadius: "8px",
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.3),
                },
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: alpha(theme.palette.divider, 0.1),
                borderRadius: "8px",
              },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  my: 6,
                }}
              >
                <CircularProgress
                  size={60}
                  thickness={4}
                  sx={{
                    color: theme.palette.primary.main,
                    mb: 3,
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: alpha(theme.palette.text.primary, 0.7),
                    fontFamily: "'Poppins', sans-serif",
                    textAlign: "center",
                  }}
                >
                  Loading form data...
                </Typography>
              </Box>
            ) : error && !error.includes("Email already exists") && !error.includes("Username already exists") ? (
              <Box sx={{ my: 3 }}>
                <Alert
                  severity="error"
                  sx={{
                    width: "100%",
                    borderRadius: "12px",
                    mb: 3,
                  }}
                >
                  {error}
                </Alert>
                {!error.includes("Email already exists") &&
                  !error.includes("Username already exists") &&
                  !error.includes("Phone number is already used") && (
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                      onClick={() => window.location.reload()}
                      sx={{
                        borderRadius: "12px",
                        textTransform: "none",
                        py: 1.5,
                        fontWeight: 600,
                        fontFamily: "'Poppins', sans-serif",
                      }}
                    >
                      Refresh Page
                    </Button>
                  )}
              </Box>
            ) : (
              <Box sx={{ 
                mt: 1,
                width: '100%',
                '& .MuiFormControl-root': {
                  width: '100%'
                },
                '& .MuiOutlinedInput-root': {
                  width: '100%'
                }
              }}>
                <FormSectionTitle>Personal Information</FormSectionTitle>
                <Grid 
                  container 
                  spacing={3}
                  sx={{
                    width: '100%',
                    margin: 0,
                    padding: 0,
                    boxSizing: 'border-box',
                    '& .MuiGrid-item': {
                      paddingTop: '24px !important',
                      paddingBottom: '0 !important'
                    }
                  }}
                >
                  <Grid 
                    item 
                    xs={12} 
                    sx={{
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      width: '100%',
                      gap: 1.5,
                      boxSizing: 'border-box',
                      '& .MuiFormControl-root': {
                        width: '100%',
                        flex: 1
                      },
                      '& .MuiOutlinedInput-root': {
                        width: '100%'
                      }
                    }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.12),
                          color: theme.palette.primary.main,
                          mt: 1.5,
                          width: 28,
                          height: 28,
                          flexShrink: 0
                        }}
                      >
                        <PersonIcon fontSize="small" />
                      </Avatar>
                      <FormControl sx={{ width: '100%' }}>
                      <StyledTextField
                        fullWidth
                        label="Full Name"
                        {...register("name", {
                          required: "Full name is required",
                          pattern: {
                            value: /^[A-Za-z\s]+$/,
                            message: "Name should only contain letters and spaces"
                          },
                          minLength: {
                            value: 2,
                            message: "Name must be at least 2 characters long"
                          },
                          maxLength: {
                            value: 50,
                            message: "Name cannot exceed 50 characters"
                          }
                        })}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                          sx={{ width: '100%' }}
                      />
                      </FormControl>
                    </Box>
                  </Grid>

                  {/* Username field only for managers */}
                  {userType === "manager" && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start',
                        width: '100%',
                        '& .MuiFormControl-root': {
                          flex: 1,
                          minWidth: 0
                        }
                      }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.12),
                            color: theme.palette.secondary.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                            flexShrink: 0
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <StyledTextField
                          fullWidth
                          label="Username"
                          {...register("username", {
                            required: userType === "manager" ? "Username is required" : false,
                            minLength: {
                              value: 3,
                              message: "Username must be at least 3 characters",
                            },
                            validate: {
                              noCapitalStart: (value) =>
                                !/^[A-Z]/.test(value) ||
                                "Username should not start with a capital letter"
                            }
                          })}
                          error={!!errors.username}
                          helperText={errors.username?.message}
                        />
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} md={userType === "manager" ? 6 : 12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      width: '100%',
                      '& .MuiFormControl-root': {
                        flex: 1,
                        minWidth: 0
                      }
                    }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.main,
                          mr: 1.5,
                          mt: 1.5,
                          width: 28,
                          height: 28,
                          flexShrink: 0
                        }}
                      >
                        <PhoneIcon fontSize="small" />
                      </Avatar>
                      <FormControl fullWidth error={!!errors.phoneNumber}>
                        <Controller
                        name="phoneNumber"
                        control={control}
                        defaultValue={watch("phoneNumber") || ""}
                          rules={{
                          required: "Phone number is required",
                          pattern: {
                            value: /^[0-9]{10}$/,
                              message: "Please enter a valid 10-digit phone number"
                            }
                          }}
                          render={({ field }) => (
                            <StyledTextField
                              {...field}
                              fullWidth
                              label="Phone Number"
                              placeholder="Enter phone number"
                              error={!!errors.phoneNumber}
                              helperText={errors.phoneNumber?.message}
                              inputProps={{
                                maxLength: 10
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      width: '100%',
                      '& .MuiFormControl-root': {
                        flex: 1,
                        minWidth: 0
                      }
                    }}>
                      <Avatar
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.12),
                          color: theme.palette.info.main,
                          mr: 1.5,
                          mt: 1.5,
                          width: 28,
                          height: 28,
                          flexShrink: 0
                        }}
                      >
                        <span style={{ fontSize: "16px" }}>@</span>
                      </Avatar>
                      <StyledTextField
                        fullWidth
                        label="Email"
                        type="email"
                        {...register("email", {
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address",
                          },
                        })}
                        error={!!errors.email}
                        helperText={errors.email?.message}
                      />
                    </Box>
                  </Grid>
                </Grid>

                {userType === "borrower" && (
                  <>
                    <FormSectionTitle>Borrower Type</FormSectionTitle>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                              color: theme.palette.warning.main,
                              mr: 1.5,
                              mt: 1.5,
                              width: 28,
                              height: 28,
                            }}
                          >
                            <MoneyIcon fontSize="small" />
                          </Avatar>
                          <FormControl fullWidth error={!!errors.userType}>
                            <InputLabel
                              sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 500,
                                fontSize: "0.9rem",
                              }}
                            >
                              Type of Borrower
                            </InputLabel>
                            <Controller
                              name="userType"
                              control={control}
                              defaultValue={watch("userType") || ""}
                              rules={{
                                required: "Type of borrower is required",
                                validate: value => value !== "none" || "Please select Individual or Entity"
                              }}
                              render={({ field }) => (
                                <StyledSelect
                                  label="Type of Borrower *"
                                  {...field}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    const newUserType = e.target.value;
                                    setSelectedUserType(newUserType);
                                    setValue("userType", newUserType);
                                    if (newUserType !== "entity") {
                                      setValue("entityType", ""); // Clear entityType
                                    }
                                  }}
                                >
                                  {BORROWER_TYPES
                                    .filter(
                                      (type) =>
                                        !HIDE_INDIVIDUAL_BORROWER_OPTION ||
                                        type.value !== "individual"
                                    )
                                    .map((type) => (
                                    <MenuItem key={type.value} value={type.value}>
                                      {type.label}
                                    </MenuItem>
                                  ))}
                                </StyledSelect>
                              )}
                            />
                            {errors.userType && (
                              <FormHelperText
                                sx={{
                                  ml: 1,
                                  mt: 0.5,
                                  fontFamily: "'Poppins', sans-serif",
                                }}
                              >
                                {errors.userType.message}
                              </FormHelperText>
                            )}
                          </FormControl>
                        </Box>
                      </Grid>

                      {/* --- entityType select (only if entity is selected) --- */}
                      {selectedUserType === "entity" && (
                        <>
                        <Grid item xs={12}>
                          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.12),
                                color: theme.palette.info.main,
                                mr: 1.5,
                                mt: 1.5,
                                width: 28,
                                height: 28,
                              }}
                            >
                              <BusinessIcon fontSize="small" />
                            </Avatar>
                            <FormControl fullWidth error={!!errors.entityType}>
                              <InputLabel
                                sx={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                              >
                                Type of Entity
                              </InputLabel>
                              <Controller
                                name="entityType"
                                control={control}
                                defaultValue={watch("entityType") || ""}
                                rules={{
                                  required: {
                                    value: selectedUserType === "entity",
                                    message: "Type of entity is required for entity borrowers",
                                  },
                                }}
                                render={({ field }) => (
                                  <StyledSelect
                                    label="Type of Entity"
                                    {...field}
                                    onChange={(e) => {
                                      field.onChange(e);
                                      setValue("entityType", e.target.value);
                                    }}
                                  >
                                    <MenuItem value="">
                                      <em>None</em>
                                    </MenuItem>
                                    {ENTITY_TYPES.map((type) => (
                                      <MenuItem key={type.value} value={type.value}>
                                        {type.label}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                )}
                              />
                              {errors.entityType && (
                                <FormHelperText
                                  sx={{
                                    ml: 1,
                                    mt: 0.5,
                                    fontFamily: "'Poppins', sans-serif",
                                  }}
                                >
                                  {errors.entityType.message}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Box>
                        </Grid>
                          <Grid item xs={12}>
                            <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                              <Avatar
                                sx={{
                                  bgcolor: alpha(theme.palette.warning.main, 0.12),
                                  color: theme.palette.warning.main,
                                  mr: 1.5,
                                  mt: 1.5,
                                  width: 28,
                                  height: 28,
                                }}
                              >
                                <BusinessIcon fontSize="small" />
                              </Avatar>
                              <FormControl fullWidth error={!!errors.entityName}>
                                <Controller
                                  name="entityName"
                                  control={control}
                                  defaultValue={watch("entityName") || ""}
                                  rules={{
                                    required: {
                                      value: selectedUserType === "entity",
                                      message: "Entity name is required for entity borrowers",
                                    },
                                  }}
                                  render={({ field }) => (
                                    <StyledTextField
                                      {...field}
                                      label="Entity Name"
                                      placeholder="Enter entity name"
                                      error={!!errors.entityName}
                                      helperText={errors.entityName?.message}
                                    />
                                  )}
                                />
                              </FormControl>
                            </Box>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <FormSectionTitle>Business Details</FormSectionTitle>
                    <Grid container spacing={2.5}>
                      {selectedUserType === "entity" && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.warning.main, 0.12),
                                color: theme.palette.warning.main,
                                mr: 1.5,
                                mt: 1.5,
                                width: 28,
                                height: 28,
                              }}
                            >
                              <BusinessIcon fontSize="small" />
                            </Avatar>
                            <StyledTextField
                              fullWidth
                              label="GST Number"
                              {...register("gstNumber")}
                              error={!!errors.gstNumber}
                              helperText={errors.gstNumber?.message}
                            />
                          </Box>
                        </Grid>
                      )}
                      <Grid item xs={12} md={selectedUserType === "entity" ? 6 : 12}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.12),
                              color: theme.palette.info.main,
                              mr: 1.5,
                              mt: 1.5,
                              width: 28,
                              height: 28,
                            }}
                          >
                            <LocationIcon fontSize="small" />
                          </Avatar>
                          <StyledTextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={2}
                            {...register("address")}
                            error={!!errors.address}
                            helperText={errors.address?.message}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(
                                theme.palette.secondary.main,
                                0.12
                              ),
                              color: theme.palette.secondary.main,
                              mr: 1.5,
                              mt: 1.5,
                              width: 28,
                              height: 28,
                            }}
                          >
                            <UploadIcon fontSize="small" />
                          </Avatar>
                          <Box sx={{ width: "100%" }}>
                            <input
                              key={fileInputKey}
                              ref={fileInputRef}
                              type="file"
                              accept="image/*,.pdf"
                              style={{ display: "none" }}
                              onChange={handleFileChange}
                              id="file-upload"
                            />
                            <FormSectionTitle>Profile Picture</FormSectionTitle>
                            <Button
                              variant="outlined"
                              component="label"
                              htmlFor="file-upload"
                              startIcon={<UploadIcon />}
                              sx={{
                                borderRadius: "12px",
                                padding: "10px 16px",
                                width: "100%",
                                justifyContent: "flex-start",
                                color: theme.palette.text.primary,
                                borderColor: alpha(theme.palette.divider, 0.5),
                              }}
                            >
                              {selectedFile
                                ? selectedFile.name
                                : existingFileUrl
                                  ? "Change Document"
                                  : "Upload Document"}
                            </Button>

                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ display: "block", mt: 1, pl: 2 }}
                            >
                              {selectedFile ? (
                                `File selected: ${selectedFile.name}`
                              ) : existingFileUrl ? (
                                <>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mt: 1,
                                    }}
                                  >
                                    <Typography variant="body2" color="primary">
                                      Existing document uploaded
                                    </Typography>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        window.open(existingFileUrl, "_blank")
                                      }
                                      sx={{ ml: 1 }}
                                    >
                                      View
                                    </Button>
                                  </Box>
                                </>
                              ) : (
                                "Supported formats: JPG, PNG, PDF"
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                )}

                {userType === "lender" && (
                  <>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2, mt: 1 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.12),
                            color: theme.palette.secondary.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <BusinessIcon fontSize="small" />
                        </Avatar>
                        <Controller
                          name="financialInstitution"
                          control={control}
                          rules={{ required: "Institution name is required" }}
                          render={({ field, fieldState: { error } }) => (
                            <StyledTextField
                              fullWidth
                              label="Institution Name"
                              value={field.value || ""}
                              onChange={field.onChange}
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2, mt: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                            color: theme.palette.primary.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <BankIcon fontSize="small" />
                        </Avatar>
                        <FormControl fullWidth error={!!errors.financialInstitutionType}>
                          <InputLabel>Financial Institution Type</InputLabel>
                          <Controller
                            name="financialInstitutionType"
                            control={control}
                            rules={{ required: "Financial institution type is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledSelect
                                label="Financial Institution Type"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                              >
                                {FINANCIER_TYPES.map((type) => (
                                  <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                          {errors.financialInstitutionType && (
                            <FormHelperText>
                              {errors.financialInstitutionType.message}
                            </FormHelperText>
                          )}

                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.secondary.main, 0.12),
                            color: theme.palette.secondary.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <MoneyIcon fontSize="small" />
                        </Avatar>
                        <FormControl fullWidth error={!!errors.typeOfLending}>
                          <InputLabel>Type of Lending</InputLabel>
                          <Controller
                            name="typeOfLending"
                            control={control}
                            rules={{ required: "Type of lending is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledSelect
                                label="Type of Lending"
                                value={field.value || ""}
                                onChange={field.onChange}
                                error={!!error}
                              >
                                <MenuItem value="SECURED">Secured</MenuItem>
                                <MenuItem value="UNSECURED">Unsecured</MenuItem>
                                <MenuItem value="BOTH">Both</MenuItem>
                              </StyledSelect>
                            )}
                          />
                          {errors.typeOfLending && (
                            <FormHelperText>
                              {errors.typeOfLending.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.12),
                            color: theme.palette.info.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <MoneyIcon fontSize="small" />
                        </Avatar>
                        <FormControl fullWidth error={!!errors.typeOfRetailLoans}>
                          <InputLabel>Type of Retail Loans</InputLabel>
                          <Controller
                            name="typeOfRetailLoans"
                            control={control}
                            rules={{ required: "Type of retail loans is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledSelect
                                label="Type of Retail Loans"
                                value={field.value || ""}
                                onChange={field.onChange}
                                error={!!error}
                              >
                                <MenuItem value="PERSONAL">Personal Loans</MenuItem>
                                <MenuItem value="BUSINESS">Business Loans</MenuItem>
                                <MenuItem value="BOTH">Both</MenuItem>
                              </StyledSelect>
                            )}
                          />
                          {errors.typeOfRetailLoans && (
                            <FormHelperText>
                              {errors.typeOfRetailLoans.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.warning.main, 0.12),
                            color: theme.palette.warning.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <BusinessIcon fontSize="small" />
                        </Avatar>
                        <Controller
                          name="designation"
                          control={control}
                          rules={{ required: "Designation is required" }}
                          render={({ field, fieldState: { error } }) => (
                            <StyledTextField
                              fullWidth
                              label="Designation"
                              value={field.value || ""}
                              onChange={field.onChange}
                              error={!!error}
                              helperText={error?.message}
                            />
                          )}
                        />
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(theme.palette.success.main, 0.12),
                            color: theme.palette.success.main,
                            mr: 1.5,
                            mt: 1.5,
                            width: 28,
                            height: 28,
                          }}
                        >
                          <LocationIcon fontSize="small" />
                        </Avatar>
                        <FormControl fullWidth error={!!errors.location}>
                          <InputLabel>Location</InputLabel>
                          <Controller
                            name="location"
                            control={control}
                            rules={{ required: "Location is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledSelect
                                label="Location"
                                value={field.value || ""}
                                onChange={field.onChange}
                                error={!!error}
                              >
                                {regions.map((region) => (
                                  <MenuItem key={region} value={region}>
                                    {region}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                          {errors.location && (
                            <FormHelperText>
                              {errors.location.message}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Box>
                    </Grid>
                  </>
                )}
                {/* Role & Location section only for managers */}
                {userType === "manager" && (
                  <>
                    <FormSectionTitle>Role & Location</FormSectionTitle>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.main,
                              mr: 1.5,
                              mt: 1.5,
                              width: 28,
                              height: 28,
                            }}
                          >
                            <BusinessIcon fontSize="small" />
                          </Avatar>
                          <FormControl fullWidth error={!!errors.roleId}>
                            <InputLabel
                              sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 500,
                                fontSize: "0.9rem",
                              }}
                            >
                              Role
                            </InputLabel>
                            <Controller
                              name="roleId"
                              control={control}
                              rules={{
                                required:
                                  userType === "manager" ? "Role is required" : false,
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <StyledSelect
                                  label="Role"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    const selected = roles.find(r => r.roleId === e.target.value);
                                    setSelectedRole(selected?.role);
                                    setValue('region', '');
                                    setValue('area', '');
                                  }}
                                  error={!!error}
                                >
                                  {roles.map((role) => (
                                    <MenuItem
                                      key={role.roleId}
                                      value={role.roleId}
                                    >
                                      {role.role}
                                    </MenuItem>
                                  ))}
                                </StyledSelect>
                              )}
                            />
                            {error && (
                              <FormHelperText error>{error.message}</FormHelperText>
                            )}
                          </FormControl>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          <Avatar
                            sx={{
                              bgcolor: alpha(theme.palette.warning.main, 0.12),
                              color: theme.palette.warning.main,
                              mr: 1.5,
                              mt: 1.5,
                              width: 28,
                              height: 28,
                            }}
                          >
                            <LocationIcon fontSize="small" />
                          </Avatar>
                          <FormControl fullWidth error={!!errors.region}>
                            <InputLabel
                              sx={{
                                fontFamily: "'Poppins', sans-serif",
                                fontWeight: 500,
                                fontSize: "0.9rem",
                              }}
                            >
                              Region
                            </InputLabel>
                            <Controller
                              name="region"
                              control={control}
                              rules={{
                                required: userType === "manager" ? "Region is required" : false,
                              }}
                              render={({ field, fieldState: { error } }) => (
                                <StyledSelect
                                  label="Region"
                                  value={field.value || ""}
                                  onChange={(e) => {
                                    field.onChange(e.target.value);
                                    fetchAreas(e.target.value);
                                  }}
                                  error={!!error}
                                >
                                  {regions.map((region) => (
                                    <MenuItem
                                      key={region}
                                      value={region}
                                    >
                                      {region}
                                    </MenuItem>
                                  ))}
                                </StyledSelect>
                              )}
                            />
                            {error && (
                              <FormHelperText error>{error.message}</FormHelperText>
                            )}
                          </FormControl>
                        </Box>
                      </Grid>

                      {selectedRole === "AREA_MANAGER" && watch("region") && (
                        <Grid item xs={12} md={6}>
                          <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                            <Avatar
                              sx={{
                                bgcolor: alpha(theme.palette.info.main, 0.12),
                                color: theme.palette.info.main,
                                mr: 1.5,
                                mt: 1.5,
                                width: 28,
                                height: 28,
                              }}
                            >
                              <LocationIcon fontSize="small" />
                            </Avatar>
                            <FormControl fullWidth error={!!errors.area}>
                              <InputLabel
                                sx={{
                                  fontFamily: "'Poppins', sans-serif",
                                  fontWeight: 500,
                                  fontSize: "0.9rem",
                                }}
                              >
                                Area
                              </InputLabel>
                              <Controller
                                name="area"
                                control={control}
                                rules={{
                                  required: selectedRole === "AREA_MANAGER" ? "Area is required" : false,
                                }}
                                render={({ field, fieldState: { error } }) => (
                                  <StyledSelect
                                    label="Area"
                                    value={field.value || ""}
                                    onChange={field.onChange}
                                    error={!!error}
                                  >
                                    {areas.map((area) => (
                                      <MenuItem
                                        key={area}
                                        value={area}
                                      >
                                        {area}
                                      </MenuItem>
                                    ))}
                                  </StyledSelect>
                                )}
                              />
                              {error && (
                                <FormHelperText error>{error.message}</FormHelperText>
                              )}
                            </FormControl>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}

                {userType === "consultant" && (
                  <>
                    <FormSectionTitle>Consultant Details</FormSectionTitle>
                    <Grid container spacing={2.5}>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Company / Firm Name (if applicable)"
                          {...register("companyName")}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <StyledTextField
                          fullWidth
                          label="Professional Website (if any)"
                          {...register("website")}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth error={!!errors.yearsExperience}>
                          <InputLabel>Years of Experience</InputLabel>
                          <Controller
                            name="yearsExperience"
                            control={control}
                            rules={{ required: "Years of Experience is required" }}
                            render={({ field }) => (
                              <StyledSelect
                                label="Years of Experience"
                                value={field.value || ""}
                                onChange={field.onChange}
                              >
                                <MenuItem value="1-3 Years">1-3 Years</MenuItem>
                                <MenuItem value="3-5 Years">3-5 Years</MenuItem>
                                <MenuItem value="5-10 Years">5-10 Years</MenuItem>
                                <MenuItem value="10+ Years">10+ Years</MenuItem>
                              </StyledSelect>
                            )}
                          />
                          {errors.yearsExperience && (
                            <FormHelperText>{errors.yearsExperience.message}</FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <InputLabel>Primary Loan Specialization(s)</InputLabel>
                          <Controller
                            name="primarySpecializations"
                            control={control}
                            rules={{
                              validate: (val) => (Array.isArray(val) && val.length > 0) || "Select at least one specialization",
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledSelect
                                multiple
                                label="Primary Loan Specialization(s)"
                                value={field.value}
                                onChange={field.onChange}
                                error={!!error}
                                renderValue={(selected) => (selected || []).join(", ")}
                              >
                                {[
                                  "Retail Loans (HL, Education Loan etc)",
                                  "Loan Against Property",
                                  "Working Capital Loans (MSME/SME)",
                                  "Project Financing",
                                  "Equipment financing",
                                  "Wholesale Lending",
                                  "Others",
                                ].map((opt) => (
                                  <MenuItem key={opt} value={opt}>
                                    {opt}
                                  </MenuItem>
                                ))}
                              </StyledSelect>
                            )}
                          />
                        </FormControl>
                      </Grid>
                      {Array.isArray(watch("primarySpecializations")) && watch("primarySpecializations").includes("Others") && (
                        <Grid item xs={12}>
                          <Controller
                            name="specializationOther"
                            control={control}
                            rules={{ required: "Please specify 'Others'" }}
                            render={({ field, fieldState: { error } }) => (
                              <StyledTextField
                                {...field}
                                fullWidth
                                label="Please Specify"
                                error={!!error}
                                helperText={error?.message}
                              />
                            )}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </>
                )}

              </Box>
            )}
          </DialogContent>
          <DialogActions
            sx={{
              px: 4,
              py: 3,
              background: alpha(theme.palette.primary.main, 0.03),
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              position: "relative",
              gap: 2,
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: "10%",
                right: "10%",
                height: "1px",
                background: `linear-gradient(90deg, 
                                    transparent 0%, 
                                    ${alpha(
                  theme.palette.primary.main,
                  0.3
                )} 50%, 
                                    transparent 100%)`,
              },
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3,
                py: 1.25,
                fontWeight: 600,
                fontSize: "0.95rem",
                borderColor: alpha(theme.palette.divider, 0.5),
                color: theme.palette.text.secondary,
                fontFamily: "'Poppins', sans-serif",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                px: 3.5,
                py: 1.25,
                fontWeight: 600,
                fontSize: "0.95rem",
                boxShadow: `0 8px 20px ${alpha(
                  theme.palette.primary.main,
                  0.25
                )}`,
                background: `linear-gradient(90deg, 
                                ${theme.palette.primary.main} 0%, 
                                ${theme.palette.primary.dark} 100%)`,
                transition: "all 0.3s ease",
                border: "none",
                fontFamily: "'Poppins', sans-serif",
                "&:hover": {
                  boxShadow: `0 10px 25px ${alpha(
                    theme.palette.primary.main,
                    0.35
                  )}`,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {mode === "add" 
                ? `Add ${userType === "manager" ? "Manager" : userType === "consultant" ? "Consultant" : userType === "borrower" ? "Borrower" : "Lender"}`
                : "Save Changes"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <OTPVerification
        showOTPVerification={showOTPVerification}
        setShowOTPVerification={setShowOTPVerification}
        showMobileOTP={showMobileOTP}
        requestData={otpRequestData}
        onVerificationSuccess={handleOTPVerificationSuccess}
        onVerificationError={handleOTPVerificationError}
      />
    </>
  );
};

export default UserForm;
